import formatDuration from 'date-fns/formatDuration';
import intlFormat from 'date-fns/intlFormat';
import parseISO from 'date-fns/parseISO';
import { apply, normalize, parse, toSeconds, toString } from 'duration-fns';
import { DateTime, Duration as LuxonDuration } from 'luxon'

/**
 * durationToString
 *
 * Takes an ISO duration string and formats it as a time/date string
 *
 * @param duration - ISO Duration string
 * @param options - Options for formatting the duration
 * @returns
 */
const durationToString = (
    duration: string,
    options?: {
        format?: string[];
        zero?: boolean;
        delimiter?: string;
        locale?: Locale;
    },
): string => {
    const result = parse(duration);

    return formatDuration(result, options);
};

/**
 * humanReadableDate
 *
 * Converts ISO date string to a human readable date
 *
 * @param dateString - An ISO DateTime string
 * @returns A human-readable date string
 */
const humanReadableDate = (dateString: string) => {
    const date = parseISO(dateString);

    return intlFormat(date, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
    });
};

/**
 * calculateDurationFromNow
 *
 * Applies the given duration to the current time to get a Date in the future
 *
 * @param duration - An ISO 8601 duration string
 * @returns A Date object
 */
const calculateDurationFromNow = (duration: string) =>
    apply(new Date(), parse(duration));

/**
 * destructureDurationString
 *
 * Break a ISO Duration string into an object of { hours, minutes, seconds }
 *
 * @param duration - ISO Duration string
 * @returns Object containing the time units
 */
const destructureDurationString = (duration?: string, showHours = false) => {
    if (!duration)
        return {
            hours: '',
            minutes: '',
            seconds: '',
        };

    const result = parse(duration);

    const total = toSeconds(result);

    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    if (showHours) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        return {
            seconds: String(seconds),
            hours: String(hours),
            minutes: String(remainingMinutes),
        };
    }

    return { hours: '', minutes: String(minutes), seconds: String(seconds) };
};

/**
 * normalizeDuration
 *
 * Normalizes a duration object and returns the ISO duration string
 *
 * Example:
 * PT27H0M0S or { hours: 27 } becomes P1DT3H0M0S
 *
 * @param duration - A Duration object
 * @returns ISO Duration string
 */
const normalizeDuration = (duration: Duration) => toString(normalize(duration));


/**
 * convertISODurationToFutureISODate
 *
 * Adds a duration in ISO format to the date and time right now, and returns a future date in ISO format
 *
 * @param isoDuration - ISO duration string
 * @returns ISO date string
 */
export const convertISODurationToFutureISODate = (isoDuration: string) => {
    const duration = LuxonDuration.fromISO(isoDuration).toObject();
    return DateTime.now().plus(duration).toISO();
}

/* --------------------------------- Export --------------------------------- */

export const dates = {
    durationToString,
    humanReadableDate,
    calculateDurationFromNow,
    destructureDurationString,
    normalizeDuration,
};
